/**
 * LES ENSEMBLES-SOLUTIONS EN π ET LES ENSEMBLES PÉRIODIQUES — tranche T45
 * (`CAP-ENSEMBLE-PI`, chapitre `MAT-5-IDENT`).
 *
 * `ensembles.ts` compare des réunions d'intervalles à bornes RATIONNELLES. Une
 * équation trigonométrique a pour solutions des nombres en π (`{π/6 ; 5π/6}`
 * sur [0 ; 2π[), et, sur ℝ, une infinité : `x = π/6 + 2kπ ou x = 5π/6 + 2kπ`,
 * k entier. Ce module lit et compare ces deux écritures, EXACTEMENT, sans
 * modèle ni calcul formel.
 *
 * ─── Le nombre exact ────────────────────────────────────────────────────────
 *
 * Une somme de termes rationnels sur une BASE : `1`, `π`, `√m` (m sans facteur
 * carré). `\dfrac{5\pi}{6}`, `-\dfrac{\sqrt{3}}{2}`, `1 + \sqrt{2}`, `0{,}52`.
 * Deux nombres sont égaux quand leurs termes le sont : π et √2 étant
 * irrationnels (et π transcendant), c'est l'égalité des nombres eux-mêmes.
 * Un produit de deux irrationnels (`\pi\sqrt{2}`) n'est pas lu.
 *
 * ─── La famille ─────────────────────────────────────────────────────────────
 *
 * `x = origine + k·période`, k entier : `\dfrac{\pi}{6} + 2k\pi`,
 * `\dfrac{k\pi}{2}`, `-\dfrac{\pi}{3} + 2\pi k`. Deux RÉUNIONS de familles sont
 * comparées sur une période commune P (le plus petit commun multiple des
 * périodes) : chaque famille y laisse un ou plusieurs restes dans [0 ; P[, et
 * deux réunions sont égales quand leurs restes le sont. Ainsi
 * `-π/3 + 2kπ` = `5π/3 + 2kπ`, et `π/4 + kπ` = `π/4 + 2kπ ou 5π/4 + 2kπ`.
 *
 * ─── Pourquoi une extension de `ensemble_solution`, pas une nature ───────────
 *
 * La question posée à l'élève est la même (« résous »), sa saisie aussi (le
 * signe `=` et « ou » de la saisie en inégalités), et son écriture se lit en
 * mots comme les autres. Un ensemble rationnel se lit TOUJOURS d'abord par
 * `lireEnsemble` : aucun verdict d'avant ne change. Ce module ne prend que ce
 * que `lireEnsemble` refuse.
 */
import { ecrireEnInegalites, ecrireEnIntervalles, lireEnsemble, memesEnsembles, nettoyerEcritureDEnsemble, } from './ensembles.js';
/* ───────────────────────────── les rationnels ───────────────────────────── */
class Hors extends Error {
}
const pgcd = (a, b) => (b === 0 ? Math.abs(a) : pgcd(b, a % b));
function q(n, d = 1) {
    if (!Number.isSafeInteger(n) || !Number.isSafeInteger(d) || d === 0)
        throw new Hors();
    const g = pgcd(n, d) || 1;
    const s = d < 0 ? -1 : 1;
    return { n: (s * n) / g, d: (s * d) / g };
}
const plusQ = (a, b) => q(a.n * b.d + b.n * a.d, a.d * b.d);
const foisQ = (a, b) => q(a.n * b.n, a.d * b.d);
const surQ = (a, b) => {
    if (b.n === 0)
        throw new Hors();
    return q(a.n * b.d, a.d * b.n);
};
const UN = '';
const PI = 'π';
function exact(termes) {
    const m = new Map();
    for (const [base, c] of termes) {
        const avant = m.get(base);
        const somme = avant === undefined ? c : plusQ(avant, c);
        if (somme.n === 0)
            m.delete(base);
        else
            m.set(base, somme);
    }
    return m;
}
const ZERO = new Map();
const rationnelExact = (r) => exact([[UN, r]]);
const plus = (a, b) => exact([...a, ...b]);
const echelle = (a, r) => exact([...a].map(([b, c]) => [b, foisQ(c, r)]));
const moins = (a, b) => plus(a, echelle(b, q(-1)));
/** Le nombre, s'il est rationnel ; `null` sinon. */
function enRationnel(a) {
    if (a.size === 0)
        return q(0);
    if (a.size === 1 && a.has(UN))
        return a.get(UN);
    return null;
}
function fois(a, b) {
    const ra = enRationnel(a);
    if (ra !== null)
        return echelle(b, ra);
    const rb = enRationnel(b);
    if (rb !== null)
        return echelle(a, rb);
    throw new Hors(); // π·√2, √2·√3 : hors de ce que le module lit
}
const valeurDeBase = (base) => base === UN ? 1 : base === PI ? Math.PI : Math.sqrt(Number(base.slice(1)));
/** La valeur approchée, pour RANGER et pour reconnaître une approximation — jamais pour juger l'égalité. */
export function valeurApprochee(a) {
    let v = 0;
    for (const [base, c] of a)
        v += (c.n / c.d) * valeurDeBase(base);
    return v;
}
/** La clé d'égalité : deux nombres exacts égaux ont la même. */
export function cleExacte(a) {
    return [...a]
        .map(([b, c]) => `${b}:${c.n}/${c.d}`)
        .sort()
        .join('|');
}
/** √r, r rationnel positif ou nul : `(s/d)·√m`, m sans facteur carré. */
function racine(r) {
    if (r.n < 0)
        throw new Hors();
    if (r.n === 0)
        return ZERO;
    let reste = r.n * r.d; // √(n/d) = √(n·d)/d
    if (!Number.isSafeInteger(reste) || reste > 1e12)
        throw new Hors();
    let dehors = 1;
    for (let f = 2; f * f <= reste; f += 1) {
        while (reste % (f * f) === 0) {
            reste /= f * f;
            dehors *= f;
        }
    }
    const coef = q(dehors, r.d);
    return reste === 1 ? rationnelExact(coef) : exact([[`√${reste}`, coef]]);
}
/**
 * `x/y`, quand c'est un rationnel (x et y proportionnels sur leurs bases) ;
 * `null` sinon. `y` nul : `null`.
 */
function rapport(x, y) {
    const premiere = [...y][0];
    if (premiere === undefined)
        return null;
    if (x.size === 0)
        return q(0);
    const r = surQ(x.get(premiere[0]) ?? q(0), premiere[1]);
    for (const base of new Set([...x.keys(), ...y.keys()])) {
        const attendu = foisQ(y.get(base) ?? q(0), r);
        const vu = x.get(base) ?? q(0);
        if (attendu.n !== vu.n || attendu.d !== vu.d)
            return null;
    }
    return r;
}
function jetons(s, parametre) {
    const sortie = [];
    let i = 0;
    while (i < s.length) {
        const reste = s.slice(i);
        const c = s[i];
        if (c === ' ') {
            i += 1;
            continue;
        }
        const nombre = /^(\d+)(?:[.,](\d+))?/.exec(reste);
        if (nombre !== null) {
            const chiffres = nombre[2] ?? '';
            if (nombre[1].length + chiffres.length > 15)
                throw new Hors();
            sortie.push({ t: 'nombre', r: q(Number(`${nombre[1]}${chiffres}`), 10 ** chiffres.length) });
            i += nombre[0].length;
            continue;
        }
        const commande = /^\\([a-zA-Z]+)/.exec(reste);
        if (commande !== null) {
            const nom = commande[1];
            const t = nom === 'pi'
                ? 'pi'
                : nom === 'sqrt'
                    ? 'racine'
                    : nom === 'frac'
                        ? 'frac'
                        : nom === 'cdot' || nom === 'times'
                            ? 'fois'
                            : nom === 'div'
                                ? 'sur'
                                : null;
            if (t === null)
                throw new Hors();
            sortie.push({ t });
            i += commande[0].length;
            continue;
        }
        if (c === parametre && !/^[a-zA-Z]/.test(s.slice(i + 1))) {
            sortie.push({ t: 'k' });
            i += 1;
            continue;
        }
        const signe = {
            π: 'pi',
            '√': 'racine',
            '×': 'fois',
            '·': 'fois',
            '⋅': 'fois',
            '*': 'fois',
            '/': 'sur',
            '÷': 'sur',
            '+': '+',
            '-': '-',
            '(': '(',
            ')': ')',
            '{': '{',
            '}': '}',
        };
        const t = signe[c];
        if (t === undefined || t === 'nombre')
            throw new Hors();
        sortie.push({ t });
        i += 1;
    }
    return sortie;
}
/** Une descente récursive : somme, produit, facteur. Lève `Hors` au moindre doute. */
class Lecteur {
    j;
    i = 0;
    constructor(j) {
        this.j = j;
    }
    get courant() {
        return this.j[this.i];
    }
    fini() {
        return this.i >= this.j.length;
    }
    /** Le signe `+` ou `-` sous le curseur, consommé ; `null` sinon. */
    signe() {
        const t = this.courant?.t;
        if (t !== '+' && t !== '-')
            return null;
        this.i += 1;
        return t;
    }
    somme() {
        let negatif = false;
        for (let s = this.signe(); s !== null; s = this.signe())
            if (s === '-')
                negatif = !negatif;
        let v = this.produit();
        if (negatif)
            v = { c: echelle(v.c, q(-1)), k: echelle(v.k, q(-1)) };
        for (let s = this.signe(); s !== null; s = this.signe()) {
            const moinsDevant = s === '-';
            const d = this.produit();
            v = moinsDevant
                ? { c: moins(v.c, d.c), k: moins(v.k, d.k) }
                : { c: plus(v.c, d.c), k: plus(v.k, d.k) };
        }
        return v;
    }
    produit() {
        let v = this.facteur();
        for (;;) {
            const t = this.courant?.t;
            if (t === 'fois') {
                this.i += 1;
                v = multiplier(v, this.facteur());
            }
            else if (t === 'sur') {
                this.i += 1;
                v = diviser(v, this.facteur());
            }
            else if (t === 'pi' ||
                t === 'k' ||
                t === 'racine' ||
                t === 'frac' ||
                t === '(' ||
                t === '{') {
                v = multiplier(v, this.facteur()); // `2k\pi`, `5\pi`, `2\sqrt{3}`
            }
            else {
                return v;
            }
        }
    }
    facteur() {
        const j = this.courant;
        if (j === undefined)
            throw new Hors();
        this.i += 1;
        switch (j.t) {
            case 'nombre':
                return { c: rationnelExact(j.r), k: ZERO };
            case 'pi':
                return { c: exact([[PI, q(1)]]), k: ZERO };
            case 'k':
                return { c: ZERO, k: rationnelExact(q(1)) };
            case 'racine': {
                const dedans = this.argument();
                const r = enRationnel(dedans.c);
                if (dedans.k.size > 0 || r === null)
                    throw new Hors();
                return { c: racine(r), k: ZERO };
            }
            case 'frac': {
                const haut = this.argument();
                return diviser(haut, this.argument());
            }
            case '(':
            case '{': {
                const v = this.somme();
                if (this.courant?.t !== (j.t === '(' ? ')' : '}'))
                    throw new Hors();
                this.i += 1;
                return v;
            }
            default:
                throw new Hors();
        }
    }
    /** L'argument d'une racine ou d'une fraction : un groupe, ou un seul nombre (`√3`). */
    argument() {
        const j = this.courant;
        if (j?.t === '{' || j?.t === '(')
            return this.facteur();
        if (j?.t === 'nombre') {
            this.i += 1;
            return { c: rationnelExact(j.r), k: ZERO };
        }
        throw new Hors();
    }
}
function multiplier(a, b) {
    if (a.k.size > 0 && b.k.size > 0)
        throw new Hors(); // k² : pas une famille
    if (b.k.size === 0)
        return { c: fois(a.c, b.c), k: fois(a.k, b.c) };
    return { c: fois(b.c, a.c), k: fois(b.k, a.c) };
}
function diviser(a, b) {
    const r = enRationnel(b.c);
    if (b.k.size > 0 || r === null || r.n === 0)
        throw new Hors(); // on ne divise que par un rationnel
    const inverse = q(r.d, r.n);
    return { c: echelle(a.c, inverse), k: echelle(a.k, inverse) };
}
function lireAffine(s, parametre) {
    try {
        const l = new Lecteur(jetons(s, parametre));
        const v = l.somme();
        return l.fini() ? v : null;
    }
    catch (e) {
        if (e instanceof Hors)
            return null;
        throw e;
    }
}
/** Un nombre exact écrit par un élève ou un auteur : `\dfrac{5\pi}{6}`, `-\sqrt{2}`, `0{,}5`. */
export function lireNombreExact(brut) {
    const a = lireAffine(nettoyerEcritureDEnsemble(brut), '\u0000');
    return a === null ? null : a.c;
}
/** Le paramètre des familles : `k`, comme au programme. */
const PARAMETRE = 'k';
const MORCEAUX_MAX = 12;
/** « , k ∈ ℤ », « (k entier) », « où k est un entier » — la fin qui DIT k, à retirer avant de lire. */
const QUANTIFICATEUR = /\s*[,;]?\s*\(?\s*(?:o[uù]\s+|avec\s+|pour\s+)?k\s*(?:(?:\\in|∈)\s*(?:\\mathbb\s*\{Z\}|ℤ|Z)|(?:est\s+)?(?:un\s+)?entier(?:\s+relatif)?)\s*\)?\s*\.?$/i;
/**
 * Lit un ensemble FINI d'éléments exacts (`{π/6 ; 5π/6}`, `x = π/4 ou x = 5π/4`)
 * ou une réunion de FAMILLES (`x = π/6 + 2kπ ou x = 5π/6 + 2kπ, k ∈ ℤ`). `null` :
 * rien de lisible — une autre variable, une inégalité, un produit d'irrationnels.
 */
export function lireEnsembleExact(brut, variable = 'x') {
    const s = nettoyerEcritureDEnsemble(brut).replace(QUANTIFICATEUR, '').trim();
    if (s === '')
        return null;
    const extension = /^\{(.*)\}$/.exec(s);
    if (extension !== null) {
        const dedans = extension[1].trim();
        if (dedans === '')
            return null;
        const elements = dedans.includes(';') ? dedans.split(';') : dedans.split(/,\s/);
        if (elements.length > MORCEAUX_MAX)
            return null;
        const points = [];
        for (const e of elements) {
            const a = lireAffine(e.trim(), '\u0000');
            if (a === null)
                return null;
            points.push(a.c);
        }
        return { ensemble: { points, familles: [] }, ecriture: 'extension' };
    }
    const morceaux = s.split(/\s+ou\s+/i);
    if (morceaux.length > MORCEAUX_MAX)
        return null;
    const points = [];
    const familles = [];
    for (const m of morceaux) {
        const membres = m.split('=').map((x) => x.trim());
        if (membres.length !== 2)
            return null;
        const [g, d] = membres;
        const ecrit = g === variable ? d : d === variable ? g : null;
        if (ecrit === null || ecrit === '')
            return null;
        const a = lireAffine(ecrit, PARAMETRE);
        if (a === null)
            return null;
        if (a.k.size === 0) {
            points.push(a.c);
            continue;
        }
        familles.push({
            origine: a.c,
            periode: valeurApprochee(a.k) < 0 ? echelle(a.k, q(-1)) : a.k,
        });
    }
    return { ensemble: { points, familles }, ecriture: 'egalites' };
}
const plcm = (a, b) => (a / pgcd(a, b)) * b;
/** Le plus petit multiple commun de rationnels positifs. */
function ppcmQ(rs) {
    let n = 1;
    let d = 0;
    for (const r of rs) {
        n = plcm(n, r.n);
        d = pgcd(d, r.d);
        if (!Number.isSafeInteger(n))
            throw new Hors();
    }
    return q(n, d);
}
/** Restes au-delà desquels une période commune n'est plus comparée (π/360 dans 2π, déjà beaucoup). */
const RESTES_MAX = 720;
/** `x` ramené dans [0 ; P[. */
function reste(x, P) {
    const r = rapport(x, P);
    const tours = r !== null ? Math.floor(r.n / r.d) : Math.floor(valeurApprochee(x) / valeurApprochee(P));
    return moins(x, echelle(P, q(tours)));
}
/** La forme sous laquelle deux ensembles se comparent, sur la période commune `P`. */
function canon(e, P) {
    const restes = new Set();
    for (const f of e.familles) {
        const m = rapport(P, f.periode);
        if (m.d !== 1 || m.n > RESTES_MAX)
            throw new Hors();
        for (let j = 0; j < m.n; j += 1) {
            restes.add(cleExacte(reste(plus(f.origine, echelle(f.periode, q(j))), P)));
        }
    }
    const points = new Set();
    for (const p of e.points) {
        const couvert = e.familles.some((f) => {
            const r = rapport(moins(p, f.origine), f.periode);
            return r !== null && r.d === 1;
        });
        if (!couvert)
            points.add(cleExacte(p));
    }
    return { restes, points };
}
const memesCles = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
/** Deux ensembles exacts sont-ils le même ensemble de réels ? */
export function memesEnsemblesExacts(a, b) {
    try {
        const familles = [...a.familles, ...b.familles];
        let P = null;
        if (familles.length > 0) {
            const u = familles[0].periode;
            const rapports = familles.map((f) => rapport(f.periode, u));
            if (rapports.some((r) => r === null || r.n <= 0))
                return false; // périodes incommensurables
            P = echelle(u, ppcmQ(rapports));
        }
        const ca = canon(a, P);
        const cb = canon(b, P);
        return memesCles(ca.restes, cb.restes) && memesCles(ca.points, cb.points);
    }
    catch (e) {
        if (e instanceof Hors)
            return false;
        throw e;
    }
}
/* ───────────────────────────── l'écriture ───────────────────────────── */
const LATEX_DE_BASE = (base) => base === PI ? '\\pi' : base === UN ? '' : `\\sqrt{${base.slice(1)}}`;
/** Un terme `c·base`, sans son signe : `\dfrac{5\pi}{6}`, `2\sqrt{3}`, `\dfrac{1}{2}`. */
function terme(c, base, k = false) {
    const n = Math.abs(c.n);
    const lettres = `${k ? 'k' : ''}${LATEX_DE_BASE(base)}`;
    const haut = lettres === '' ? String(n) : `${n === 1 ? '' : n}${lettres}`;
    return c.d === 1 ? haut : `\\dfrac{${haut}}{${c.d}}`;
}
/** Les termes, rationnel d'abord, puis les radicaux, puis π. */
function termesRanges(a) {
    const rang = (b) => (b === UN ? 0 : b === PI ? 2 : 1);
    return [...a].sort(([x], [y]) => rang(x) - rang(y) || x.localeCompare(y));
}
function ecrireTermes(a, k, suite) {
    return termesRanges(a)
        .map(([base, c], i) => {
        const signe = c.n < 0 ? (i === 0 && !suite ? '-' : ' - ') : i === 0 && !suite ? '' : ' + ';
        return `${signe}${terme(c, base, k)}`;
    })
        .join('');
}
/** Un nombre exact en LaTeX restreint : `\dfrac{5\pi}{6}`, `-\dfrac{\pi}{3}`, `1 + \sqrt{2}`, `0`. */
export function ecrireNombreExact(a) {
    return a.size === 0 ? '0' : ecrireTermes(a, false, false);
}
/** Une famille en LaTeX restreint : `\dfrac{\pi}{6} + 2k\pi`, `\dfrac{k\pi}{2}`. */
export function ecrireFamille(f) {
    if (f.origine.size === 0)
        return ecrireTermes(f.periode, true, false);
    return `${ecrireNombreExact(f.origine)}${ecrireTermes(f.periode, true, true)}`;
}
/**
 * L'ensemble, en TEXTE RICHE (formules entre `\( \)`) : `\(\{\dfrac{\pi}{6} ; \dfrac{5\pi}{6}\}\)`,
 * ou `\(x = \dfrac{\pi}{6} + 2k\pi\) ou \(x = \dfrac{5\pi}{6} + 2k\pi\), \(k \in \mathbb{Z}\)`.
 */
export function ecrireEnsembleExact(lu, variable = 'x') {
    const { points, familles } = lu.ensemble;
    if (lu.ecriture === 'extension' && familles.length === 0) {
        return `\\(\\{${points.map(ecrireNombreExact).join(' ; ')}\\}\\)`;
    }
    const egalites = [
        ...points.map((p) => `\\(${variable} = ${ecrireNombreExact(p)}\\)`),
        ...familles.map((f) => `\\(${variable} = ${ecrireFamille(f)}\\)`),
    ].join(' ou ');
    return familles.length > 0 ? `${egalites}, \\(k \\in \\mathbb{Z}\\)` : egalites;
}
export function lireEnsembleSolution(brut, variable = 'x') {
    const reel = lireEnsemble(brut, variable);
    if (reel !== null)
        return { sorte: 'reel', lu: reel };
    const lu = lireEnsembleExact(brut, variable);
    return lu === null ? null : { sorte: 'exact', lu };
}
/** Un ensemble fini à bornes rationnelles, en ensemble exact ; `null` s'il porte un intervalle. */
function versExact(l) {
    if (l.sorte === 'exact')
        return l.lu.ensemble;
    const points = [];
    for (const i of l.lu.ensemble) {
        if (i.inf === null || i.sup === null || !i.infInclus || !i.supInclus)
            return null;
        if (i.inf.n * i.sup.d !== i.sup.n * i.inf.d)
            return null;
        points.push(rationnelExact(i.inf));
    }
    return { points, familles: [] };
}
/** Deux ensembles-solutions sont-ils le même ensemble ? */
export function memesEnsemblesSolutions(a, b) {
    if (a.sorte === 'reel' && b.sorte === 'reel')
        return memesEnsembles(a.lu.ensemble, b.lu.ensemble);
    const ea = versExact(a);
    const eb = versExact(b);
    return ea !== null && eb !== null && memesEnsemblesExacts(ea, eb);
}
/** L'ensemble, récrit dans l'écriture où il a été donné ; texte riche pour un ensemble exact. */
export function ecrireEnsembleSolution(l, variable = 'x') {
    if (l.sorte === 'exact')
        return ecrireEnsembleExact(l.lu, variable);
    return l.lu.ecriture === 'intervalle' || l.lu.ecriture === 'extension'
        ? ecrireEnIntervalles(l.lu.ensemble)
        : ecrireEnInegalites(l.lu.ensemble, variable);
}
/** Les éléments d'un ensemble FINI, rangés ; `null` s'il porte une famille ou un intervalle. */
function elementsFinis(l) {
    const e = versExact(l);
    if (e === null || e.familles.length > 0)
        return null;
    const vus = new Map(e.points.map((p) => [cleExacte(p), p]));
    return [...vus.values()].sort((x, y) => valeurApprochee(x) - valeurApprochee(y));
}
/**
 * L'élève a-t-il donné des VALEURS APPROCHÉES des éléments attendus ?
 * (`{0,52 ; 2,62}` pour `{π/6 ; 5π/6}`) — au centième près. Le constat dit
 * alors « valeur exacte attendue » : la réponse n'est pas juste.
 */
export function approcheLEnsemble(eleve, attendu) {
    const a = elementsFinis(eleve);
    const b = elementsFinis(attendu);
    if (a === null || b === null || a.length !== b.length || a.length === 0)
        return false;
    if (b.every((x) => enRationnel(x) !== null))
        return false;
    return a.every((x, i) => Math.abs(valeurApprochee(x) - valeurApprochee(b[i])) <= 0.0101);
}
/**
 * L'énoncé demande un ARRONDI (`arrondi`: décimales) : chaque élément de
 * l'élève est l'élément attendu, exact, ou son arrondi à ces décimales.
 */
export function memesALArrondi(eleve, attendu, decimales) {
    const a = elementsFinis(eleve);
    const b = elementsFinis(attendu);
    if (a === null || b === null || a.length !== b.length)
        return false;
    const pas = 10 ** decimales;
    return a.every((x, i) => {
        const y = b[i];
        if (cleExacte(x) === cleExacte(y))
            return true;
        const r = enRationnel(x);
        if (r === null)
            return false;
        const arrondi = Math.round(valeurApprochee(y) * pas) / pas;
        return Math.abs(r.n / r.d - arrondi) < 1e-9;
    });
}
//# sourceMappingURL=ensembles-exacts.js.map